import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useId } from 'react';
import { Search, X, Filter, Calendar, User, Building, FileText, Briefcase, Dot, FileDiff, SearchIcon, ChevronDown, Loader2, Pin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../styles/EnhancedSearch.css';
import { TextJustify } from '@mynaui/icons-react';
import { Checkbox } from './FieldComponents';
import { fetchNavSearchResults } from '../utils/navSearch';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';

const DEFAULT_FILTERS = {
    type: 'cases',
    status: 'all',
    dateRange: 'all',
    assignedTo: 'all',
    includeArchived: false
};

const ENHANCED_SEARCH_FILTERS_STORAGE_KEY = 'enhanced_search_filters_v1';
const NAVIGATION_PINS_URL = 'https://api.casedb.co/navigation-pins.php';

const resultIdentity = (result) => `${result?.type || ''}:${result?.value ?? result?.id ?? ''}`;

const normalizePinnedResult = (item) => ({
    ...item,
    id: item?.value ?? item?.id,
    value: item?.value ?? item?.id,
    label: item?.label || item?.case_name || item?.contact_name || `${item?.type === 'lead' ? 'Lead' : 'Case'} ${item?.value ?? item?.id}`,
    contactName: item?.contactName ?? item?.contact_name ?? '',
    assignedUser: item?.assignedUser ?? item?.assigned_user ?? '',
    assignedUserUsername: item?.assignedUserUsername ?? item?.assigned_user_username ?? '',
    createdDate: item?.createdDate ?? item?.created_date ?? null,
    updatedDate: item?.updatedDate ?? item?.updated_at ?? null,
    caseType: item?.caseType ?? item?.case_type ?? null,
    isOnTeam: Boolean(item?.isOnTeam ?? item?.is_on_team ?? false),
    pinned: true,
});

const sortPinnedResults = (results) => [...results].sort((left, right) => {
    const labelOrder = String(left?.label || '').localeCompare(String(right?.label || ''), undefined, {
        sensitivity: 'base',
    });
    if (labelOrder !== 0) return labelOrder;

    const typeOrder = String(left?.type || '').localeCompare(String(right?.type || ''));
    if (typeOrder !== 0) return typeOrder;

    return String(left?.value ?? left?.id ?? '').localeCompare(String(right?.value ?? right?.id ?? ''), undefined, {
        numeric: true,
    });
});

export const EnhancedSearch = ({ expanded = false, setExpanded = () => {}, modal = false, onChange, initiallyActive = false, selectionMode = false, caseOnly = false, enablePinning = false }) => {
    const [isActive, setIsActive] = useState(initiallyActive);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [pinnedResults, setPinnedResults] = useState([]);
    const [loadingPins, setLoadingPins] = useState(enablePinning);
    const [pinningResultKey, setPinningResultKey] = useState('');
    const [pinError, setPinError] = useState('');
    const [phases, setPhases] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState(() => {
        if (modal) {
            return DEFAULT_FILTERS;
        }

        try {
            const storedFilters = localStorage.getItem(ENHANCED_SEARCH_FILTERS_STORAGE_KEY);
            if (!storedFilters) {
                return DEFAULT_FILTERS;
            }

            const parsedFilters = JSON.parse(storedFilters);
            return {
                ...DEFAULT_FILTERS,
                ...parsedFilters
            };
        } catch (error) {
            console.warn('Failed to restore enhanced search filters', error);
            return DEFAULT_FILTERS;
        }
    });
    const [loading, setLoading] = useState(false);
    const [selectedResult, setSelectedResult] = useState(null);
    const [highlightedResultIndex, setHighlightedResultIndex] = useState(-1);
    const [panelReady, setPanelReady] = useState(false);

    const searchRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const resultsListId = useId();
    const navigate = useNavigate();

    useEffect(() => {
        fetchRecentSearches();
        fetchPhasesAndStatuses();
    }, []);

    useEffect(() => {
        if (!enablePinning) {
            setPinnedResults([]);
            setLoadingPins(false);
            return undefined;
        }

        let cancelled = false;
        const loadPinnedResults = async () => {
            setLoadingPins(true);
            try {
                const response = await fetch(NAVIGATION_PINS_URL, {
                    credentials: 'include'
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok || data?.success === false) {
                    throw new Error(data?.message || 'Unable to load pinned cases and leads');
                }
                if (cancelled) return;

                const nextResults = Array.isArray(data?.pinned_results)
                    ? data.pinned_results.map(normalizePinnedResult)
                    : [];
                setPinnedResults(sortPinnedResults(nextResults));
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching navigation pins:', error);
                }
            } finally {
                if (!cancelled) {
                    setLoadingPins(false);
                }
            }
        };

        loadPinnedResults();
        return () => {
            cancelled = true;
        };
    }, [enablePinning]);

    // Handle clicks outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsActive(false);
            }
        };

        // Only add listener when dropdown is active
        if (isActive) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Cleanup
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isActive]);

    // Focus input when expanded changes from false to true
    useEffect(() => {
        if (expanded && !modal && inputRef.current && isActive) {
            // Small delay to ensure the input is rendered and visible
            const timer = setTimeout(() => {
                if (inputRef.current && document.activeElement !== inputRef.current) {
                    inputRef.current.focus();
                }
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [expanded, modal, isActive]);

    useEffect(() => {
        if (!initiallyActive) {
            return;
        }

        setIsActive(true);

        const timer = setTimeout(() => {
            if (inputRef.current && document.activeElement !== inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);

        return () => clearTimeout(timer);
    }, [initiallyActive]);

    // Keep a newly mounted panel hidden for its first layout frame. This lets
    // parent overlays and responsive width rules settle before the user can
    // see the dropdown, preventing the brief off-screen position flash.
    useLayoutEffect(() => {
        if (!isActive) {
            setPanelReady(false);
            return undefined;
        }

        setPanelReady(false);
        const frame = window.requestAnimationFrame(() => setPanelReady(true));
        return () => window.cancelAnimationFrame(frame);
    }, [expanded, isActive, modal]);

    useEffect(() => {
        if (modal) {
            return;
        }

        try {
            localStorage.setItem(ENHANCED_SEARCH_FILTERS_STORAGE_KEY, JSON.stringify(filters));
        } catch (error) {
            console.warn('Failed to persist enhanced search filters', error);
        }
    }, [filters, modal]);

    const fetchRecentSearches = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/recent-searches.php`, {
                credentials: 'include'
            });
            const data = await response.json();
            
            if (data.recent_searches) {
                setRecentSearches(data.recent_searches);
            }
        } catch (error) {
            console.error('Error fetching recent searches:', error);
        }
    };

    const addToRecentSearches = async (item) => {
        try {
            const response = await fetch(`https://api.casedb.co/recent-searches.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    search_item: {
                        id: item.id,
                        label: item.label,
                        type: item.type,
                        value: item.value
                    }
                })
            });
            
            if (response.ok) {
                // Refresh recent searches
                fetchRecentSearches();
            }
        } catch (error) {
            console.error('Error adding to recent searches:', error);
        }
    };

    const updatePinnedResult = async (result, pinned) => {
        const entityId = Number(result?.value ?? result?.id);
        const entityType = String(result?.type || '').toLowerCase();
        const resultKey = resultIdentity(result);
        if (!enablePinning || pinningResultKey || !['case', 'lead'].includes(entityType) || !Number.isInteger(entityId) || entityId <= 0) {
            return;
        }

        setPinError('');
        setPinningResultKey(resultKey);
        try {
            await ensureCaseDbCsrfToken().catch(() => null);
            const response = await fetch(NAVIGATION_PINS_URL, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({
                    entity_type: entityType,
                    entity_id: entityId,
                    pinned,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || data?.success === false) {
                throw new Error(data?.message || 'Unable to update that pin');
            }

            const nextResults = Array.isArray(data?.pinned_results)
                ? data.pinned_results.map(normalizePinnedResult)
                : [];
            setPinnedResults(sortPinnedResults(nextResults));
        } catch (error) {
            console.error('Error updating navigation pin:', error);
            setPinError(error?.message || 'Unable to update that pin.');
        } finally {
            setPinningResultKey('');
        }
    };

    const fetchPhasesAndStatuses = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/phases.php`, {
                credentials: 'include'
            });
            const data = await response.json();
            
            if (data.phases) {
                setPhases(data.phases);
            }
            if (data.statuses) {
                setStatuses(data.statuses);
            }
        } catch (error) {
            console.error('Error fetching phases and statuses:', error);
        }
    };

    const normalizeText = (value) => {
        if (!value) return '';
        return value
            .toString()
            .normalize('NFKD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    };

    const pinnedResultKeys = useMemo(
        () => new Set(pinnedResults.map(resultIdentity)),
        [pinnedResults]
    );

    const emptySearchResults = useMemo(() => {
        if (!enablePinning) {
            return recentSearches;
        }

        const unpinnedRecentSearches = recentSearches.filter((result) => !pinnedResultKeys.has(resultIdentity(result)));
        return [
            ...sortPinnedResults(pinnedResults).map((result) => ({ ...result, pinned: true })),
            ...unpinnedRecentSearches.map((result) => ({ ...result, pinned: false })),
        ];
    }, [enablePinning, pinnedResultKeys, pinnedResults, recentSearches]);

    const resultsForDisplay = searchTerm.trim() ? searchResults : emptySearchResults;

    const displayedResults = useMemo(() => {
        if (!Array.isArray(resultsForDisplay) || resultsForDisplay.length === 0) {
            return [];
        }

        const isExactIdMatch = searchTerm && resultsForDisplay.some(result =>
            result.value?.toString() === searchTerm.trim()
        );

        const shouldIncludeArchived = filters.includeArchived || isExactIdMatch;

        return resultsForDisplay.filter(result => {
            if (caseOnly && result.type !== 'case') {
                return false;
            }

            return (result.phase === "Archived" && shouldIncludeArchived) ||
                result.phase !== "Archived";
        });
    }, [caseOnly, filters.includeArchived, resultsForDisplay, searchTerm]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            setLoading(false);
            return;
        }

        let isCancelled = false;

        const runSearch = async () => {
            setLoading(true);
            try {
                const resultType = caseOnly ? 'cases' : (filters.type === 'all' ? 'both' : filters.type);
                const results = await fetchNavSearchResults(searchTerm, { type: resultType, limit: 50 });
                if (isCancelled) {
                    return;
                }

                const isExactIdMatch = results.combined.some(result => result.value?.toString() === searchTerm.trim());
                const allowArchived = filters.includeArchived || isExactIdMatch;

                const filteredResults = results.combined.filter(item => {
                    if (!allowArchived && item.phase === 'Archived') {
                        return false;
                    }

                    if (filters.status !== 'all') {
                        if (item.type === 'case') {
                            return item.phase === filters.status;
                        }
                        if (item.type === 'lead') {
                            return item.status === filters.status;
                        }
                    }

                    return true;
                });

                setSearchResults(filteredResults);
            } catch (error) {
                if (!isCancelled) {
                    console.error('Error searching cases and leads:', error);
                    setSearchResults([]);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        runSearch();

        return () => {
            isCancelled = true;
        };
    }, [caseOnly, filters.includeArchived, filters.status, filters.type, searchTerm]);

    // Handle search input focus
    const handleFocus = () => {
        if (!modal) {
            setExpanded(true);
        }
        setIsActive(true);
        // Focus immediately if modal
        if (modal && inputRef.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Handle search input blur
    const handleBlur = (e) => {
        // Don't close if focus is moving to another element within the search component
        if (!searchRef.current?.contains(e.relatedTarget)) {
            setTimeout(() => setIsActive(false), 200);
        }
    };

    const handleInputKeyDown = (e) => {
        if (!isActive) {
            return;
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            setIsActive(false);
            setHighlightedResultIndex(-1);
            return;
        }

        if (e.key === 'Enter' && selectionMode) {
            e.preventDefault();
            if (highlightedResultIndex >= 0 && displayedResults[highlightedResultIndex]) {
                handleResultSelect(displayedResults[highlightedResultIndex], { copyOnly: e.shiftKey });
            }
            return;
        }

        if (displayedResults.length === 0) {
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedResultIndex((prev) => {
                if (prev < 0) return 0;
                return (prev + 1) % displayedResults.length;
            });
            return;
        }

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedResultIndex((prev) => {
                if (prev < 0) return displayedResults.length - 1;
                return prev <= 0 ? displayedResults.length - 1 : prev - 1;
            });
            return;
        }

        if (e.key === 'Enter' && highlightedResultIndex >= 0 && displayedResults[highlightedResultIndex]) {
            e.preventDefault();
            handleResultSelect(displayedResults[highlightedResultIndex], { copyOnly: e.shiftKey });
        }
    };

    // Handle result selection
    const copyResultId = async (result) => {
        if (!result?.value) {
            return;
        }

        try {
            await navigator.clipboard.writeText(String(result.value));
        } catch (error) {
            console.error('Failed to copy result ID:', error);
        }
    };

    const handleResultSelect = async (result, options = {}) => {
        const { copyOnly = false } = options;

        if (copyOnly) {
            await copyResultId(result);
            setIsActive(false);
            setExpanded(false);
            setHighlightedResultIndex(-1);
            if (!modal) {
                setSearchTerm('');
                setSearchResults([]);
            }
            return;
        }

        // Add to recent searches
        addToRecentSearches(result);

        if (selectionMode) {
            setSelectedResult(null);
            setSearchTerm('');
            setSearchResults([]);
            setHighlightedResultIndex(-1);
            setIsActive(true);
            if (onChange) {
                onChange(result);
            }
            setTimeout(() => inputRef.current?.focus(), 0);
            return;
        }

        setExpanded(false);
        
        if (modal) {
            // In modal mode, store the selection and update search term to show selected item
            setSelectedResult(result);
            setSearchTerm(result.label);
            setSearchResults([]);
            setIsActive(false);
            // Remove focus from input to properly close dropdown
            if (inputRef.current) {
                inputRef.current.blur();
            }
        } else {
            // Regular navigation mode
            if (result.type === 'case') {
                navigate(`/case/${result.value}`);
            } else if (result.type === 'lead') {
                navigate(`/intake/${result.value}`);
            }
            setIsActive(false);
            setSearchTerm('');
            setSearchResults([]);
        }
        
        if (onChange) {
            onChange(result);
        }
    };

    const handleResultMouseDown = (result) => (e) => {
        if (e.button === 1) {
            e.preventDefault();
            openResultInNewTab(result);
            return;
        }

        if (e.button !== 0) {
            return;
        }

        e.preventDefault();
        handleResultSelect(result, { copyOnly: e.shiftKey });
    };

    useEffect(() => {
        if (!isActive || displayedResults.length === 0) {
            setHighlightedResultIndex(-1);
            return;
        }

        setHighlightedResultIndex((prev) => {
            if (prev >= 0 && prev < displayedResults.length) {
                return prev;
            }
            return 0;
        });
    }, [displayedResults, isActive]);

    useEffect(() => {
        if (!isActive || highlightedResultIndex < 0 || !dropdownRef.current) {
            return;
        }

        const activeResult = dropdownRef.current.querySelector(`[data-result-index="${highlightedResultIndex}"]`);
        if (activeResult?.scrollIntoView) {
            activeResult.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedResultIndex, isActive]);

    const openResultInNewTab = (result) => {
        if (!result) return;
        if (result.type === 'case') {
            window.open(`/case/${result.value}`, '_blank', 'noopener,noreferrer');
        } else if (result.type === 'lead') {
            window.open(`/intake/${result.value}`, '_blank', 'noopener,noreferrer');
        }
    };

    // Handle clear search
    const handleClear = () => {
        setSearchTerm('');
        setSearchResults([]);
        if (modal && !selectionMode) {
            setSelectedResult(null);
            if (onChange) {
                onChange(null);
            }
        }
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Handle filter changes
    const handleFilterChange = (filterKey, value) => {
        setFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
    };

    // Parse and format tags
    const formatTags = (tagsJson) => {
        if (!tagsJson) return [];
        
        try {
            const tags = JSON.parse(tagsJson);
            if (Array.isArray(tags)) {
                return tags.map(tag => {
                    const tagStr = String(tag).trim();
                    return tagStr.startsWith('#') ? tagStr : `#${tagStr}`;
                });
            }
            return [];
        } catch (error) {
            console.error('Error parsing tags:', error);
            return [];
        }
    };


    // Get icon for result type
    const getResultIcon = (type) => {
        switch (type) {
            case 'case':
                return <Briefcase size={16} className="result-icon" />;
            case 'lead':
                return <User size={16} className="result-icon" />;
            default:
                return <FileText size={16} className="result-icon" />;
        }
    };

    const shouldShowFilters = expanded && showFilters;

    const renderResultsHeaderActions = () => (
        <div className='enhanced-search-actions'>
            {!selectionMode && (
                <button
                    type="button"
                    className='filter-button'
                    onClick={() => {
                        navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
                        setExpanded(false);
                    }}
                >
                    <span className='search-deep-icon'><SearchIcon size={13} /></span>Search Deeper
                </button>
            )}

            {expanded && (
                <button
                    type="button"
                    onClick={() => setShowFilters(!showFilters)}
                    className={`filter-button ${showFilters ? 'active' : ''}`}
                    title="Toggle filters"
                >
                    <Filter size={16} />
                </button>
            )}
        </div>
    );

    return (
        <div 
            ref={searchRef}
            className={`enhanced-search ${isActive ? 'active' : ''} ${expanded ? 'nav-expanded' : 'nav-collapsed'}`}
        >
            {/* Main search input - maintains 36px height */}
            {!selectedResult && <div className="search-input-container" style={{ display: "flex", justifyContent: (!expanded && !modal) ? 'center' : 'flex-start', overflow: 'hidden' }} onClick={handleFocus}>
                <Search size={20} className="search-icon" />
                {(expanded || modal) && <input
                    ref={inputRef}
                    type="text"
                    aria-label={caseOnly ? 'Search cases' : 'Search cases and leads'}
                    role={selectionMode ? 'combobox' : undefined}
                    aria-autocomplete={selectionMode ? 'list' : undefined}
                    aria-expanded={selectionMode ? isActive && (expanded || modal) : undefined}
                    aria-controls={selectionMode ? resultsListId : undefined}
                    aria-activedescendant={
                        selectionMode && highlightedResultIndex >= 0 && displayedResults[highlightedResultIndex]
                            ? `${resultsListId}-option-${highlightedResultIndex}`
                            : undefined
                    }
                    placeholder={(expanded || modal) ? (caseOnly ? "Search cases..." : "Search cases & leads...") : ""}
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setSearchResults([]);
                        setHighlightedResultIndex(-1);
                        if (selectionMode) {
                            setIsActive(true);
                        }
                        if (modal && e.target.value !== selectedResult?.label) {
                            setSelectedResult(null);
                        }
                    }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleInputKeyDown}
                    className="search-input"
                    style={{ 
                        color: 'var(--text-color)',
                        backgroundColor: 'transparent',
                        fontSize: '14px',
                        border: 'none',
                        outline: 'none',
                        width: '100%',
                        fontFamily: 'inherit',
                        paddingRight: searchTerm ? '32px' : '0' // Add padding when clear button is present
                    }}
                />}
                {searchTerm && (expanded || modal) && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="clear-button-absolute"
                        aria-label="Clear search"
                    >
                        <X size={16} />
                    </button>
                )}
            </div>}

            {/* Selected result display for modal mode */}
            {modal && !selectionMode && selectedResult && !isActive && (
                <div className="selected-result-display" style={{
                    padding: '12px',
                    backgroundColor: 'var(--border-color)',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: '1px solid var(--hover-color)',
                    cursor: 'pointer'
                }} onClick={() => setIsActive(true)}>
                    {getResultIcon(selectedResult.type)}
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: 'var(--text-color)' }}>
                            {selectedResult.label}
                        </div>
                        <div style={{ fontSize: '0.85em', color: 'var(--text-color)', opacity: 0.7 }}>
                            {selectedResult.type === 'case' ? 'Case' : 'Lead'} ID: {selectedResult.value}
                            {selectedResult.status && ` • ${selectedResult.status}`}
                        </div>
                    </div>
                    <button 
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedResult(null);
                            setSearchTerm('');
                            if (onChange) {
                                onChange(null);
                            }
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--text-color)',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '2px'
                        }}
                        title="Clear selection"
                        aria-label="Clear selection"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Expanded search panel - position absolute, 200% width */}
            {isActive && (expanded || modal) && (
                <div
                    className={`search-panel ${modal ? 'modal-item' : ''} ${panelReady ? 'is-positioned' : 'is-positioning'}`}
                    ref={dropdownRef}
                    aria-hidden={!panelReady}
                >
                    {/* Filters section */}
                    {shouldShowFilters && (
                        <div className="filters-section">
                            {!caseOnly && <div className="filter-group" style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <label>Type:</label>
                                <div className='select-wrapper'>
                                    <select 
                                        className='filter-select'
                                        value={filters.type}
                                        onChange={(e) => handleFilterChange('type', e.target.value)}
                                    >
                                        <option style={{ color: 'var(--text-color)' }} value="all">All</option>
                                        <option style={{ color: 'var(--text-color)' }} value="cases">Cases</option>
                                        <option style={{ color: 'var(--text-color)' }} value="leads">Leads</option>
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>}
                            <div 
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    if (inputRef.current) {
                                        inputRef.current.focus();
                                    }
                                }}
                                onClick={() => {
                                    handleFilterChange('includeArchived', !filters.includeArchived);
                                    if (inputRef.current) {
                                        inputRef.current.focus();
                                    }
                                }}
                                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                            >
                                <Checkbox 
                                    label="Include Archived" 
                                    value={filters.includeArchived} 
                                    onChange={() => {}}
                                />
                            </div>
                            <div className="filter-group" style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <label>Phase:</label>
                                <div className='select-wrapper'>
                                    <select 
                                        className='filter-select'
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                    >
                                        <option style={{ color: 'var(--text-color)' }} value="all">All Statuses/Phases</option>
                                        {phases.map(phase => (
                                            <option style={{ color: 'var(--text-color)' }} key={`phase-${phase.id}`} value={phase.phase}>
                                                {phase.phase} (Case Phase)
                                            </option>
                                        ))}
                                        {statuses.map(status => (
                                            <option style={{ color: 'var(--text-color)' }} key={`status-${status.id}`} value={status.name}>
                                                {status.name} (Lead Status)
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Search results */}
                    <div className="results-section">
                        {!searchTerm && (
                            <div className="results-header">
                                <span>{enablePinning && pinnedResults.length > 0 ? 'Pinned' : 'Recent searches'}</span>
                                {renderResultsHeaderActions()}
                            </div>
                        )}
                        {pinError && (
                            <div className="search-pin-error" role="alert">{pinError}</div>
                        )}
                        {(loading || (!searchTerm && enablePinning && loadingPins)) && (
                            <div className="loading-state">
                                <span>{!searchTerm && loadingPins ? 'Loading pinned items...' : 'Searching...'}</span>
                            </div>
                        )}

                        {!loading && searchTerm && searchResults.length === 0 && (
                            <>
                                <div className="results-header">
                                    <span>Search results (0)</span>
                                    {renderResultsHeaderActions()}
                                </div>
                                <div className="no-results">
                                    <span>No results found for "{searchTerm}"</span>
                                </div>
                            </>
                        )}

                        {!loading && !(!searchTerm && enablePinning && loadingPins) && displayedResults.length > 0 && (
                            <>
                                {searchTerm && (
                                    <div className="results-header">
                                        <span>
                                            {displayedResults.length}{' '}
                                            {displayedResults.every((result) => result.phase === 'Archived') ? 'Archived ' : ''}
                                            {displayedResults.some((result) => result.phase === 'Archived') && !displayedResults.every((result) => result.phase === 'Archived')
                                                ? `(${displayedResults.filter((result) => result.phase === 'Archived').length} Archived) `
                                                : ''}
                                            result{displayedResults.length !== 1 ? 's' : ''} found
                                        </span>
                                        {renderResultsHeaderActions()}
                                    </div>
                                )}
                                <div
                                    className="results-list"
                                    id={selectionMode ? resultsListId : undefined}
                                    role={selectionMode ? 'listbox' : undefined}
                                >
                                    {displayedResults.map((result, index) => (
                                        <React.Fragment key={`${result.type}-${result.id || result.value || index}`}>
                                            {!searchTerm
                                                && enablePinning
                                                && pinnedResults.length > 0
                                                && index === displayedResults.findIndex((item) => !pinnedResultKeys.has(resultIdentity(item)))
                                                && <div className="results-header enhanced-search-recent-header"><span>Recent searches</span></div>}
                                            <div
                                                id={selectionMode ? `${resultsListId}-option-${index}` : undefined}
                                                role={selectionMode ? 'option' : undefined}
                                                aria-selected={selectionMode ? highlightedResultIndex === index : undefined}
                                                className={`result-item ${highlightedResultIndex === index ? 'active' : ''} ${!searchTerm ? 'recent-item' : ''} ${pinnedResultKeys.has(resultIdentity(result)) ? 'pinned-item' : ''} ${result.phase === 'Archived' ? 'archived-result' : ''}`}
                                                data-result-index={index}
                                                onMouseDown={handleResultMouseDown(result)}
                                            >
                                                <div className="result-content">
                                                    <div className="result-main">
                                                    {getResultIcon(result.type)}
                                                    <span className="result-title">{result.label}</span>
                                                    <span className="result-type">{result.type}<Dot size={16} />{result.value}</span>
                                                    {enablePinning && ['case', 'lead'].includes(result.type) && (
                                                        <button
                                                            type="button"
                                                            className={`result-pin-button ${pinnedResultKeys.has(resultIdentity(result)) ? 'is-pinned' : ''}`}
                                                            aria-label={`${pinnedResultKeys.has(resultIdentity(result)) ? 'Unpin' : 'Pin'} ${result.label}`}
                                                            title={`${pinnedResultKeys.has(resultIdentity(result)) ? 'Unpin' : 'Pin'} ${result.type}`}
                                                            disabled={Boolean(pinningResultKey)}
                                                            onMouseDown={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                            }}
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                updatePinnedResult(result, !pinnedResultKeys.has(resultIdentity(result)));
                                                            }}
                                                        >
                                                            {pinningResultKey === resultIdentity(result)
                                                                ? <Loader2 size={15} className="spinner" />
                                                                : <Pin size={15} fill={pinnedResultKeys.has(resultIdentity(result)) ? 'currentColor' : 'none'} />}
                                                        </button>
                                                    )}
                                                    </div>
                                                    {(result.phase || result.status || result.contactName || result.assignedUser) && (
                                                        <div className="result-meta">
                                                        {result.status && (
                                                            <span className="result-status">{result.status}</span>
                                                        )}
                                                        {result.phase && (
                                                            <span className={`result-status ${result.phase === 'Archived' ? 'archived-badge' : ''}`}>
                                                                {result.phase}
                                                            </span>
                                                        )}
                                                        {result.type === 'case' && (
                                                            <>
                                                                {result.contactName && (
                                                                    <span className="result-status">{result.contactName}</span>
                                                                )}
                                                                {result.assignedUser && (
                                                                    <span className="result-status">{result.assignedUser} {result?.assignedUserUsername && `(@${result.assignedUserUsername})`}</span>
                                                                )}
                                                                {result.tags && formatTags(result.tags).length > 0 && (
                                                                    <div className="result-tags-container">
                                                                        {formatTags(result.tags).map((tag, tagIndex) => (
                                                                            <span key={tagIndex} className="result-status">{tag}</span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {result.type === 'lead' && (
                                                            <>
                                                                {result.cases && (
                                                                    <span className="result-status">Linked Case</span>
                                                                )}
                                                                {result.createdDate && (
                                                                    <span className="result-status">{result.createdDate}</span>
                                                                )}
                                                                {result.caseType && (
                                                                    <span className="result-status">{result.caseType}</span>
                                                                )}
                                                            </>
                                                        )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </>
                        )}

                        {!loading && searchTerm && searchResults.length > 0 && displayedResults.length === 0 && (
                            <>
                                <div className="results-header">
                                    <span>Search results (0 shown)</span>
                                    {renderResultsHeaderActions()}
                                </div>
                                <div className="no-results">
                                    <span>No results found (all results are archived)</span>
                                </div>
                            </>
                        )}

                        {!loading && !loadingPins && !searchTerm && displayedResults.length === 0 && (
                            <div className="search-placeholder">
                                <span>{caseOnly ? 'Start typing to search cases' : 'Start typing to search cases and leads'}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnhancedSearch;
